import type Anthropic from '@anthropic-ai/sdk'
import type { AdvisorTool, AdvisorEvent, AdvisorMessage, ToolResult } from './types'
import { ADVISOR_MODEL } from '../config'

// ---------------------------------------------------------------------------
// SystemBlock — callers build system prompts from these.
// The last block in the stable prefix should carry cache_control:'ephemeral'
// to activate prompt caching on the relay.
// ---------------------------------------------------------------------------

export interface SystemBlock {
  type: 'text'
  text: string
  cache_control?: { type: 'ephemeral' }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Hard cap on the number of model↔tool round-trips per turn. Raised 8→12 for
 *  Advisor v2: cross-page questions legitimately chain several query_kpi calls
 *  (spec §4.4); relay telemetry informs future tuning. */
const MAX_ITERATIONS = 12

/**
 * Map an AdvisorTool to the SDK's Tool shape.
 *
 * NOTE: AdvisorTool.input_schema is typed as Record<string, unknown>, but
 * Anthropic.Tool.InputSchema requires {type:'object',...}. The cast is safe
 * because all real tools provide a valid JSON-Schema object (enforced by
 * convention), and the API will reject malformed schemas at runtime anyway.
 */
function toAnthropicTool(t: AdvisorTool): Anthropic.Tool {
  return {
    name: t.name,
    description: t.description,
    input_schema: t.input_schema as Anthropic.Tool.InputSchema,
  }
}

// ---------------------------------------------------------------------------
// runAdvisorTurn — streaming tool-use loop
// ---------------------------------------------------------------------------

/**
 * Drives one user-initiated turn through the Anthropic streaming tool-use loop.
 *
 * Algorithm (per iteration):
 *  1. Start a stream with the current messages + tools.
 *  2. Emit text/thinking events from content_block_delta events.
 *  3. Accumulate the final assistant message.
 *  4. If stop_reason !== 'tool_use', emit done and return.
 *  5. For each tool_use block: run the tool (catching errors), emit
 *     tool_start / tool_result events, collect a tool_result block.
 *  6. Append the tool_result user turn; loop.
 *  7. Cap at MAX_ITERATIONS — emit error + done, return (never loop forever).
 *
 * Any unhandled throw emits error + done before returning (no silent fallback).
 *
 * Constraints satisfied:
 * - Model: claude-opus-4-8 only
 * - Thinking: adaptive/summarized (no budget_tokens, no temperature/top_p/top_k)
 * - Always streams (messages.stream — never a non-streaming call)
 */
export async function runAdvisorTurn(opts: {
  client: Anthropic
  system: SystemBlock[]
  messages: AdvisorMessage[]
  tools: AdvisorTool[]
  onEvent: (e: AdvisorEvent) => void
  signal?: AbortSignal
}): Promise<AdvisorMessage[]> {
  const { client, system, tools, onEvent, signal } = opts
  // Work on a local copy so the caller's array is not mutated.
  let messages: AdvisorMessage[] = [...opts.messages]

  try {
    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
      // ── 1. Start stream ─────────────────────────────────────────────────
      const stream = client.messages.stream(
        {
          model: ADVISOR_MODEL,
          max_tokens: 8000,
          // adaptive thinking — no budget_tokens (would 400 on Opus 4.8)
          thinking: { type: 'adaptive', display: 'summarized' },
          // SystemBlock is structurally compatible with TextBlockParam
          system,
          messages,
          tools: tools.map(toAnthropicTool),
        },
        // Pass the AbortSignal (if provided) via RequestOptions
        { signal },
      )

      // ── 2. Emit delta events ─────────────────────────────────────────────
      for await (const event of stream) {
        if (event.type === 'content_block_delta') {
          if (event.delta.type === 'text_delta') {
            onEvent({ type: 'text', text: event.delta.text })
          } else if (event.delta.type === 'thinking_delta') {
            onEvent({ type: 'thinking', text: event.delta.thinking })
          }
        }
      }

      // ── 3. Accumulate final assistant message ────────────────────────────
      const msg = await stream.finalMessage()

      // msg.content is ContentBlock[] (API response type).  We cast to
      // MessageParam['content'] for round-tripping: response blocks are
      // structurally wire-compatible with the param shapes the API accepts.
      messages = [
        ...messages,
        {
          role: 'assistant',
          content: msg.content as Anthropic.MessageParam['content'],
        },
      ]

      // ── 4. End-of-turn? ──────────────────────────────────────────────────
      if (msg.stop_reason !== 'tool_use') {
        onEvent({ type: 'done' })
        return messages
      }

      // ── 5. Process tool calls ────────────────────────────────────────────
      const toolResults: Anthropic.ToolResultBlockParam[] = []

      for (const block of msg.content) {
        if (block.type !== 'tool_use') continue
        const { id, name, input } = block

        onEvent({ type: 'tool_start', name })

        // Resolve and run the tool; surface failures explicitly.
        const registeredTool = tools.find((t) => t.name === name)
        let result: ToolResult = { content: '' }

        if (!registeredTool) {
          // Unknown tool — surface as is_error so the model can explain.
          result = { content: `unknown tool: ${name}`, isError: true }
        } else {
          try {
            result = await registeredTool.run(input, signal)
          } catch (err) {
            // Tool threw — surface as is_error (no silent swallow).
            result = { content: String(err), isError: true }
          }
        }

        onEvent({
          type: 'tool_result',
          name,
          ui: result.ui,
          isError: result.isError,
        })

        toolResults.push({
          type: 'tool_result',
          tool_use_id: id,
          content: result.content,
          is_error: result.isError,
        })
      }

      // ── 6. Guard: tool_use stop must yield ≥1 tool_use block ─────────────
      // If stop_reason was 'tool_use' but no tool_use blocks were present, an
      // empty tool_result user turn would 400 at the real API. Surface the
      // anomaly explicitly rather than sending a malformed request.
      if (toolResults.length === 0) {
        onEvent({
          type: 'error',
          message: 'tool_use stop with no tool_use blocks',
        })
        onEvent({ type: 'done' })
        return messages
      }

      // ── 7. Append tool results user turn, loop ───────────────────────────
      // ToolResultBlockParam[] is a subtype of ContentBlockParam[] so no cast needed.
      messages = [...messages, { role: 'user', content: toolResults }]
    }

    // ── 8. Cap exceeded ──────────────────────────────────────────────────
    // Explicit error — no silent infinite loop.
    onEvent({
      type: 'error',
      message: `Advisor loop exceeded maximum iterations (${MAX_ITERATIONS})`,
    })
    onEvent({ type: 'done' })
    return messages
  } catch (err) {
    // Unhandled throw from stream or SDK — surface explicitly.
    onEvent({ type: 'error', message: String(err) })
    onEvent({ type: 'done' })
    return messages
  }
}
